import { DispatchBoardQueueLoad } from "app/data/city-delivery/dispatch-board/models";
import ShippingDocsService from "app/data/shipping-docs/shippingDocsService";
import { deliverPdf, getPdfDownloadErrorMessage } from "app/data/shipping-docs/downloadPdf";
import { PdfDocumentAction } from "app/data/shipping-docs/pdfDocumentActionStorage";
import React, { memo, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PdfActionSplitButton from "ui-components/pdf-action-split-button/pdfActionSplitButton";
import { useGetDispatchBoardQueueLoadDetails } from "../../hooks/useDispatchBoardQueueLoadDetails";

const MAX_SHIPPING_LABEL_PROBILLS = 100;

type DownloadDocumentType = "shippingLabels" | "deliveryManifest" | "driverReceipts";

export interface CityDeliveryDispatchWorkflowToolsProps {
    dispatchLoad: DispatchBoardQueueLoad;
    terminalNumber: number | undefined;
}

const shippingDocsService = ShippingDocsService.getInstance();

const CityDeliveryDispatchWorkflowTools: React.FC<CityDeliveryDispatchWorkflowToolsProps> = memo((props) => {
    const { dispatchLoad, terminalNumber } = props;
    const [downloadingType, setDownloadingType] = useState<DownloadDocumentType | null>(null);

    const hasManifestNumber =
        typeof dispatchLoad?.manifestNumber === "number" && dispatchLoad.manifestNumber !== 0;

    const { loadDetails, isLoadingLoadDetails } = useGetDispatchBoardQueueLoadDetails({
        terminalNumber,
        picklistNumber: dispatchLoad.picklistNumber,
        manifestNumber: dispatchLoad.manifestNumber,
    });

    const probillNumbers = useMemo(
        () =>
            (loadDetails ?? []).flatMap((stop) =>
                (stop.probills ?? []).map((probill) => probill.probillNumber)
            ),
        [loadDetails]
    );

    const hasProbills = probillNumbers.length > 0;
    const isDownloadingShippingLabels = downloadingType === "shippingLabels";
    const isDownloadingDeliveryManifest = downloadingType === "deliveryManifest";
    const isDownloadingDriverReceipts = downloadingType === "driverReceipts";
    const anyBusy = downloadingType != null;

    const handleShippingLabels = async (action: PdfDocumentAction) => {
        if (!hasProbills) {
            toast.error("No probills available for shipment labels.");
            return;
        }
        if (probillNumbers.length > MAX_SHIPPING_LABEL_PROBILLS) {
            toast.error(`Shipment labels are limited to ${MAX_SHIPPING_LABEL_PROBILLS} probills per download.`);
            return;
        }
        const failVerb = action === "print" ? "print" : "download";
        const pastVerb = action === "print" ? "printed" : "downloaded";
        try {
            setDownloadingType("shippingLabels");
            const response = await shippingDocsService.downloadShippingLabels({
                probillNumbers,
            });
            deliverPdf(response.data, `shipping-labels_${probillNumbers[0]}.pdf`, action);
            toast.info(`Shipment labels ${pastVerb}.`);
        } catch (error) {
            toast.error(getPdfDownloadErrorMessage(error, `Unable to ${failVerb} shipment labels.`));
        } finally {
            setDownloadingType(null);
        }
    };

    const handleDeliveryManifest = async (action: PdfDocumentAction) => {
        if (!hasManifestNumber) return;
        const failVerb = action === "print" ? "print" : "download";
        const pastVerb = action === "print" ? "printed" : "downloaded";
        try {
            setDownloadingType("deliveryManifest");
            const response = await shippingDocsService.downloadDeliveryManifest({
                manifestNumber: dispatchLoad.manifestNumber,
            });
            deliverPdf(
                response.data,
                `delivery-manifest_${dispatchLoad.manifestNumber}.pdf`,
                action
            );
            toast.info(`Delivery manifest ${pastVerb}.`);
        } catch (error) {
            toast.error(getPdfDownloadErrorMessage(error, `Unable to ${failVerb} delivery manifest.`));
        } finally {
            setDownloadingType(null);
        }
    };

    const handleDriverReceipts = async (action: PdfDocumentAction) => {
        if (!hasManifestNumber) return;
        const failVerb = action === "print" ? "print" : "download";
        const pastVerb = action === "print" ? "printed" : "downloaded";
        try {
            setDownloadingType("driverReceipts");
            const response = await shippingDocsService.downloadDriverReceiptsByManifest({
                manifestNumber: dispatchLoad.manifestNumber,
            });
            deliverPdf(
                response.data,
                `driver-receipts_${dispatchLoad.manifestNumber}.pdf`,
                action
            );
            toast.info(`Driver receipts ${pastVerb}.`);
        } catch (error) {
            toast.error(getPdfDownloadErrorMessage(error, `Unable to ${failVerb} driver receipts.`));
        } finally {
            setDownloadingType(null);
        }
    };

    return (
        <div className="city-delivery-dispatch-workflow__tools">
            <PdfActionSplitButton
                className="city-delivery-dispatch-workflow__tools__button"
                disabled={!hasProbills || isLoadingLoadDetails || anyBusy}
                spinner={isDownloadingShippingLabels}
                onAction={handleShippingLabels}
            >
                Shipment Labels
            </PdfActionSplitButton>
            {hasManifestNumber && (
                <PdfActionSplitButton
                    className="city-delivery-dispatch-workflow__tools__button"
                    disabled={anyBusy}
                    spinner={isDownloadingDeliveryManifest}
                    onAction={handleDeliveryManifest}
                >
                    Delivery Manifest
                </PdfActionSplitButton>
            )}
            {hasManifestNumber && (
                <PdfActionSplitButton
                    className="city-delivery-dispatch-workflow__tools__button"
                    disabled={anyBusy}
                    spinner={isDownloadingDriverReceipts}
                    onAction={handleDriverReceipts}
                >
                    Driver Receipts
                </PdfActionSplitButton>
            )}
        </div>
    );
});

export default CityDeliveryDispatchWorkflowTools;
